import React, { useCallback, useContext, useEffect, useState } from 'react';
import { OrderInterface } from '../../../components/OrdersTable/interfaces/Order.interface';
import {
	OrderStatesEnum,
	OrderStatusesEnum,
} from '../../../services/orders.services';
import { resolveExecutionStatus } from '../../../utils/order-execution.util';
import { NotificationsService } from '../../../services/notifications.services';
import Emitter, { EventType } from '../../../services/events';
import CreatedStep from './components/Steps/Created';
import PreparingStep from './components/Steps/Preparing';
import ToUnloadingStep from './components/Steps/ToUnloading';
import UnloadingStep from './components/Steps/Unloading';
import CompletedStep from './components/Steps/Completed';
import PendingPaymentStep from './components/Steps/PendingPayment';
import { OrderDetailsProps } from './clientOrder.interface';
import { OrderDetailsContext } from '../../../context/OrderDetailsContext/order-details.context';
import { BillingService } from '../../../services/billing.service';
import {
	Contract,
	ContractsService,
} from '../../../services/contracts.service';
import { SypacButton, SypacText } from '@sypac/component-library-react';
import ArrowToLeft from '../../../assets/ArrowToLeft';
import { T } from '@tolgee/react';

const OrderDetails: React.FC<OrderDetailsProps> = ({
	isOpen = false,
	selectedOrder,
	onBack,
	hideViewContractButton = false,
	hideMap = false,
	position = 'absolute',
}) => {
	const { setCustomerInvoice, setHideViewContractButton } =
		useContext(OrderDetailsContext);
	const [currentTab] = useState<string>('overview');
	const [contract, setContract] = useState<Contract>();
	const markReadNotifications = useCallback(async () => {
		const notificationIds = selectedOrder?.notifications?.items
			?.filter((r) => !r.readAt)
			?.map((r) => r.id);
		try {
			if (notificationIds?.length) {
				await NotificationsService.readNotify(notificationIds);
				Emitter.emit(EventType.NOTIFICATION_COUNT_DECREASE, 1);
			}
			Emitter.emit(
				EventType.ORDER_NOTIFICATION_COUNT_DECREASE,
				selectedOrder?.id!,
			);
		} catch (e) {}
	}, [selectedOrder]);

	const getInvoice = useCallback(async () => {
		if (
			!selectedOrder?.id ||
			!selectedOrder?.target ||
			(selectedOrder.deliveryIncluded
				? selectedOrder.state !== OrderStatesEnum.COMPLETED
				: selectedOrder.status !== OrderStatusesEnum.PENDING_PAYMENT)
		) {
			return;
		}

		try {
			const { data } = await BillingService.getInvoiceById(
				selectedOrder.target,
				selectedOrder.id,
			);
			setCustomerInvoice(data);
		} catch (e) {}
	}, [selectedOrder, setCustomerInvoice]);

	useEffect(() => {
		getInvoice().then(() => {});
	}, [getInvoice]);

	useEffect(() => {
		if (selectedOrder?.id) {
			markReadNotifications().then(() => {});
		}
	}, [markReadNotifications, selectedOrder?.id]);

	useEffect(() => {
		let isActive = true;

		const fetchContract = async () => {
			try {
				if (!selectedOrder?.contractId) {
					setContract(undefined);
					return;
				}

				const { data } = await ContractsService.getContractById(
					selectedOrder.contractId,
					'customer',
				);
				if (isActive) setContract(data);
			} catch (e) {}
		};

		fetchContract().then(() => {});
		return () => {
			isActive = false;
		};
	}, [selectedOrder?.contractId]);

	useEffect(() => {
		setHideViewContractButton(!!hideViewContractButton);
	}, [hideViewContractButton, setHideViewContractButton]);

	const renderSimpleSteps = (order: OrderInterface) => {
		const { status } = order;

		if (status === OrderStatusesEnum.PAID) {
			return (
				<CompletedStep
					selectedOrder={order}
					hideMap={hideMap}
					contract={contract}
					onBack={onBack}
				/>
			);
		}

		switch (resolveExecutionStatus(order)) {
			case OrderStatesEnum.TO_DO:
			case OrderStatesEnum.AWAITING_CONFIRMATION:
			case OrderStatesEnum.TRANSPORTER_CONFIRMED:
				return (
					<CreatedStep
						selectedOrder={order}
						hideMap={hideMap}
						contract={contract}
						onBack={onBack}
					/>
				);

			case OrderStatesEnum.TRUCK_CONFIRMED:
			case OrderStatusesEnum.REQUESTED:
			case OrderStatusesEnum.ACCEPTED:
			case OrderStatesEnum.TO_LOADING:
			case OrderStatusesEnum.ARRIVED_TO_LOADING:
			case OrderStatusesEnum.LOADING:
				return (
					<PreparingStep
						selectedOrder={order}
						hideMap={hideMap}
						contract={contract}
						onBack={onBack}
					/>
				);

			case OrderStatusesEnum.LOADED:
			case OrderStatesEnum.TO_UNLOADING:
				return (
					<ToUnloadingStep
						selectedOrder={order}
						hideMap={hideMap}
						contract={contract}
						onBack={onBack}
					/>
				);

			case OrderStatusesEnum.ARRIVED_TO_UNLOADING:
			case OrderStatusesEnum.UNLOADING:
			case OrderStatusesEnum.UNLOADED:
				return (
					<UnloadingStep
						selectedOrder={order}
						hideMap={hideMap}
						contract={contract}
						onBack={onBack}
					/>
				);

			case OrderStatusesEnum.COMPLETED:
				return (
					<PendingPaymentStep
						selectedOrder={order}
						hideMap={hideMap}
						contract={contract}
						onBack={onBack}
					/>
				);

			default:
				return null;
		}
	};

	const renderMultipleSteps = (order: OrderInterface) => {
		const { status, state } = order;

		if (status === OrderStatusesEnum.PAID) {
			return (
				<CompletedStep
					selectedOrder={order}
					hideMap={hideMap}
					contract={contract}
					onBack={onBack}
				/>
			);
		} else if (order?.trucks?.every((obj) => obj.orderStatus === 'completed')) {
			return (
				<UnloadingStep
					selectedOrder={order}
					hideMap={hideMap}
					contract={contract}
					onBack={onBack}
				/>
			);
		} else if (
			status === OrderStatusesEnum.PREPARING ||
			status === OrderStatusesEnum.TO_UNLOADING ||
			status === OrderStatusesEnum.ARRIVED_TO_UNLOADING ||
			status === OrderStatusesEnum.DELIVERED
		) {
			return (
				<ToUnloadingStep
					selectedOrder={order}
					hideMap={hideMap}
					contract={contract}
					onBack={onBack}
				/>
			);
		}

		switch (state) {
			case OrderStatesEnum.TO_DO:
				return (
					<CreatedStep
						selectedOrder={order}
						hideMap={hideMap}
						contract={contract}
						onBack={onBack}
					/>
				);

			case OrderStatesEnum.AWAITING_CONFIRMATION:
			case OrderStatesEnum.TRANSPORTER_CONFIRMED:
			case OrderStatesEnum.TRUCK_CONFIRMED:
				return (
					<PreparingStep
						selectedOrder={order}
						hideMap={hideMap}
						contract={contract}
						onBack={onBack}
					/>
				);

			default:
				return null;
		}
	};

	const renderSimpleExcluded = (order: OrderInterface) => {
		const { status } = order;

		switch (status) {
			case OrderStatusesEnum.CREATED:
				return (
					<CreatedStep
						selectedOrder={order}
						hideMap={hideMap}
						contract={contract}
						onBack={onBack}
					/>
				);
			case OrderStatusesEnum.PREPARING:
				return (
					<PreparingStep
						selectedOrder={order}
						hideMap={hideMap}
						contract={contract}
						onBack={onBack}
					/>
				);
			case OrderStatusesEnum.IN_PROGRESS:
				return (
					<ToUnloadingStep
						selectedOrder={order}
						hideMap={hideMap}
						contract={contract}
						onBack={onBack}
					/>
				);
			case OrderStatusesEnum.DELIVERED:
				return (
					<PendingPaymentStep
						selectedOrder={order}
						hideMap={hideMap}
						contract={contract}
						onBack={onBack}
					/>
				);
			case OrderStatusesEnum.PAID:
				return (
					<CompletedStep
						selectedOrder={order}
						hideMap={hideMap}
						contract={contract}
						onBack={onBack}
					/>
				);
			default:
				return null;
		}
	};

	const renderMultipleExcluded = (order: OrderInterface) => {
		const { status } = order;

		if (
			(status === OrderStatusesEnum.IN_PROGRESS ||
				status === OrderStatusesEnum.PREPARING) &&
			order?.trucks?.some((obj) => 'producerLoadDetails' in obj)
		) {
			return (
				<ToUnloadingStep
					selectedOrder={order}
					hideMap={hideMap}
					contract={contract}
					onBack={onBack}
				/>
			);
		}

		if (
			status === OrderStatusesEnum.DELIVERED &&
			order?.trucks?.every((obj) => 'producerLoadDetails' in obj)
		) {
			return (
				<UnloadingStep
					selectedOrder={order}
					hideMap={hideMap}
					contract={contract}
					onBack={onBack}
				/>
			);
		}

		switch (status) {
			case OrderStatusesEnum.CREATED:
				return (
					<CreatedStep
						selectedOrder={order}
						hideMap={hideMap}
						contract={contract}
						onBack={onBack}
					/>
				);
			case OrderStatusesEnum.PREPARING:
			case OrderStatusesEnum.IN_PROGRESS:
				return (
					<PreparingStep
						selectedOrder={order}
						hideMap={hideMap}
						contract={contract}
						onBack={onBack}
					/>
				);
			case OrderStatusesEnum.DELIVERED:
				return (
					<ToUnloadingStep
						selectedOrder={order}
						hideMap={hideMap}
						contract={contract}
						onBack={onBack}
					/>
				);

			case OrderStatusesEnum.PAID:
				return (
					<CompletedStep
						selectedOrder={order}
						hideMap={hideMap}
						contract={contract}
						onBack={onBack}
					/>
				);

			default:
				return null;
		}
	};

	const renderOverviewTab = (order: OrderInterface | undefined) => {
		if (!order) return null;
		const { trucks, deliveryIncluded } = order;
		const simpleOrder = trucks?.length === 1;

		return simpleOrder
			? deliveryIncluded
				? renderSimpleSteps(order)
				: renderSimpleExcluded(order)
			: deliveryIncluded
			? renderMultipleSteps(order)
			: renderMultipleExcluded(order);
	};

	return (
		<>
			{isOpen ? (
				<div
					className={`w-[716px] h-full flex flex-col ${position} top-0 right-0 bg-alabaster shadow-order-details z-[100] overflow-y-auto scroll-smooth rounded-2xl sypac-scrollbar`}
				>
					<>
						{onBack ? (
							<div className="fixed w-[716px] h-[96px] flex items-center px-4 bg-alabaster box-border z-[1000]">
								<SypacButton variant="secondary">
									<button
										type="button"
										className="flex gap-2 border-none h-[24px] bg-transparent group"
										onClick={onBack}
									>
										<span className="mt-[4px] transition group-hover:text-gray-80 group-hover:-translate-x-[6px]">
											<ArrowToLeft width={38.5} height={16.5} />
										</span>
										<SypacText variant="body-normal-medium">
											<p className="text-gray-80 mt-[2px]">
												<T keyName="clientSelectCategory.backButton">Back</T>
											</p>
										</SypacText>
									</button>
								</SypacButton>
							</div>
						) : null}

						{currentTab === 'overview' && renderOverviewTab(selectedOrder)}

						{currentTab === 'overview' ? (
							<>
								{/* <div className="flex flex-row justify-center mt-20">
									<SypacButton variant="primary" size="large">
										<button>
											<T keyName="orderDetails.chatWithSypac">
												Chat with sypac
											</T>
										</button>
									</SypacButton>
								</div> */}
							</>
						) : null}
					</>
				</div>
			) : null}
		</>
	);
};

export default OrderDetails;
